var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { FaSpinner, FaSearch, FaSave } from 'react-icons/fa';
import { DEFAULT_GENERATION_QUOTAS, GENERATION_QUOTA_LABELS, GenerationQuota, } from '@common/contracts';
import { getQuotaDefaults, updateQuotaDefaults, listUserQuotas, updateUserQuota, } from '../../api/settings';
import './GenerationLimitsManager.scss';
const QUOTA_KEYS = Object.values(GenerationQuota);
function errorMessage(err) {
    var _a, _b;
    return ((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || (err === null || err === void 0 ? void 0 : err.message) || 'Something went wrong';
}
// A small labelled row of the three quota number inputs, reused for defaults + per user.
function QuotaInputs({ value, onChange, disabled }) {
    return (_jsx("div", { className: "gl-quota-inputs", children: QUOTA_KEYS.map((k) => (_jsxs("label", { className: "gl-quota-field", children: [_jsx("span", { children: GENERATION_QUOTA_LABELS[k] }), _jsx("input", { type: "text", inputMode: "numeric", disabled: disabled, value: String(value[k]), onChange: (e) => {
                        const raw = e.target.value;
                        if (raw !== '' && !/^\d+$/.test(raw))
                            return;
                        onChange(Object.assign(Object.assign({}, value), { [k]: raw === '' ? 0 : parseInt(raw, 10) }));
                    } })] }, k))) }));
}
function GenerationLimitsManager() {
    const [defaults, setDefaults] = useState(DEFAULT_GENERATION_QUOTAS);
    const [loading, setLoading] = useState(true);
    const [savingDefaults, setSavingDefaults] = useState(false);
    const [defaultsMsg, setDefaultsMsg] = useState(null);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    // Per-user editable balances, keyed by username (seeded from the loaded rows).
    const [edits, setEdits] = useState({});
    const [savingUser, setSavingUser] = useState(null);
    useEffect(() => {
        getQuotaDefaults()
            .then(setDefaults)
            .catch((e) => setError(errorMessage(e)))
            .finally(() => setLoading(false));
        void reloadUsers('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const reloadUsers = (term) => __awaiter(this, void 0, void 0, function* () {
        setUsersLoading(true);
        try {
            const rows = yield listUserQuotas(term.trim() || undefined);
            setUsers(rows);
            setEdits(Object.fromEntries(rows.map((r) => [r.username, r.quotas])));
        }
        catch (e) {
            setError(errorMessage(e));
        }
        finally {
            setUsersLoading(false);
        }
    });
    const saveDefaults = (applyToExisting) => __awaiter(this, void 0, void 0, function* () {
        if (applyToExisting && !window.confirm('Reset EVERY user who has logged into the editor to these limits? This overwrites their remaining balances.'))
            return;
        setSavingDefaults(true);
        setDefaultsMsg(null);
        setError(null);
        try {
            const res = yield updateQuotaDefaults(defaults, applyToExisting);
            setDefaults(res.defaults);
            setDefaultsMsg(applyToExisting
                ? `Saved. Reset ${res.appliedTo} existing user${res.appliedTo === 1 ? '' : 's'}.`
                : 'Saved — applies to users who log in from now on.');
            if (applyToExisting)
                void reloadUsers(search);
        }
        catch (e) {
            setError(errorMessage(e));
        }
        finally {
            setSavingDefaults(false);
        }
    });
    const saveUser = (username) => __awaiter(this, void 0, void 0, function* () {
        setSavingUser(username);
        setError(null);
        try {
            const res = yield updateUserQuota(username, edits[username]);
            setUsers((prev) => prev.map((u) => (u.username === username ? Object.assign(Object.assign({}, u), { quotas: res.quotas }) : u)));
        }
        catch (e) {
            setError(errorMessage(e));
        }
        finally {
            setSavingUser(null);
        }
    });
    const dirty = useMemo(() => (username) => {
        const row = users.find((u) => u.username === username);
        const e = edits[username];
        return !!row && !!e && QUOTA_KEYS.some((k) => row.quotas[k] !== e[k]);
    }, [users, edits]);
    if (loading)
        return _jsx("div", { className: "generation-limits", children: _jsxs("p", { className: "gl-loading", children: [_jsx(FaSpinner, { className: "gl-spin" }), " Loading\u2026"] }) });
    return (_jsxs("div", { className: "generation-limits", children: [_jsxs("section", { className: "gl-card", children: [_jsx("h2", { children: "Default limits" }), _jsx("p", { className: "gl-sub", children: "Each generation spends one attempt (charged on success). New users who log into the editor start with these balances." }), _jsx(QuotaInputs, { value: defaults, onChange: setDefaults, disabled: savingDefaults }), _jsxs("div", { className: "gl-actions", children: [_jsx("button", { className: "gl-btn", onClick: () => saveDefaults(false), disabled: savingDefaults, children: savingDefaults ? _jsx(FaSpinner, { className: "gl-spin" }) : 'Save (new users)' }), _jsx("button", { className: "gl-btn primary", onClick: () => saveDefaults(true), disabled: savingDefaults, children: "Save & apply to all users" })] }), defaultsMsg && _jsx("p", { className: "gl-ok", children: defaultsMsg })] }), _jsxs("section", { className: "gl-card", children: [_jsx("h2", { children: "Per-user balances" }), _jsx("p", { className: "gl-sub", children: "Only users who have logged into the editor appear here. Search a username to view or overwrite what they have left." }), _jsxs("form", { className: "gl-search", onSubmit: (e) => { e.preventDefault(); void reloadUsers(search); }, children: [_jsx(FaSearch, { className: "gl-search-icon" }), _jsx("input", { value: search, onChange: (e) => setSearch(e.target.value), placeholder: "Search username\u2026" }), _jsx("button", { type: "submit", className: "gl-btn", disabled: usersLoading, children: usersLoading ? _jsx(FaSpinner, { className: "gl-spin" }) : 'Search' })] }), users.length === 0 ? (_jsx("p", { className: "gl-empty", children: usersLoading ? 'Loading…' : 'No tracked users match.' })) : (_jsxs("div", { className: "gl-user-table", style: { ['--quota-cols']: QUOTA_KEYS.length }, children: [_jsxs("div", { className: "gl-user-head", children: [_jsx("span", { children: "User" }), QUOTA_KEYS.map((k) => _jsx("span", { children: GENERATION_QUOTA_LABELS[k] }, k)), _jsx("span", {})] }), users.map((u) => (_jsxs("div", { className: "gl-user-row", children: [_jsx("span", { className: "gl-user-name", title: u.username, children: u.username }), QUOTA_KEYS.map((k) => {
                                        var _a, _b;
                                        return (_jsx("input", { type: "text", inputMode: "numeric", disabled: savingUser === u.username, value: String((_b = (_a = edits[u.username]) === null || _a === void 0 ? void 0 : _a[k]) !== null && _b !== void 0 ? _b : u.quotas[k]), onChange: (e) => {
                                                const raw = e.target.value;
                                                if (raw !== '' && !/^\d+$/.test(raw))
                                                    return;
                                                setEdits((prev) => { var _a; return (Object.assign(Object.assign({}, prev), { [u.username]: Object.assign(Object.assign({}, ((_a = prev[u.username]) !== null && _a !== void 0 ? _a : u.quotas)), { [k]: raw === '' ? 0 : parseInt(raw, 10) }) })); });
                                            } }, k));
                                    }), _jsx("button", { className: "gl-btn small", disabled: !dirty(u.username) || savingUser === u.username, onClick: () => saveUser(u.username), title: "Save this user's balances", children: savingUser === u.username ? _jsx(FaSpinner, { className: "gl-spin" }) : _jsx(FaSave, {}) })] }, u.username)))] }))] }), error && _jsx("p", { className: "gl-error", children: error })] }));
}
export default GenerationLimitsManager;
